import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DeviceMobile, Desktop, Television, SpeakerHigh,
  Plus, Trash, Lock, LockOpen, Power, X,
  Clock, ShieldCheck, ArrowsClockwise, WifiHigh
} from '@phosphor-icons/react';

const TYPE_ICONS = {
  android_phone: DeviceMobile, android_tablet: Desktop,
  led_tv: Television, smart_speaker: SpeakerHigh, other: WifiHigh,
};
const TYPE_LABELS = {
  android_phone: 'Android Phone', android_tablet: 'Tablet',
  led_tv: 'LED TV', smart_speaker: 'Speaker', other: 'Other',
};

export default function DevicesPage() {
  const { api } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRules, setShowRules] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'android_phone', owner: '', room: '', ip_address: '', mac_address: '' });
  const [rules, setRules] = useState({ daily_limit_minutes: 120, bedtime_start: '21:00', bedtime_end: '07:00', blocked_apps: [], allowed_apps: [] });

  const fetchDevices = async () => {
    try { const res = await api.get('/devices'); setDevices(res.data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchDevices(); }, [api]);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    try { await api.post('/devices', form); setShowForm(false); setForm({ name: '', type: 'android_phone', owner: '', room: '', ip_address: '', mac_address: '' }); fetchDevices(); } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this device?')) return;
    try { await api.delete(`/devices/${id}`); fetchDevices(); } catch (e) { console.error(e); }
  };

  const handleCommand = async (deviceId, command) => {
    try {
      const res = await api.post(`/devices/${deviceId}/command`, { command });
      alert(res.data.message);
      fetchDevices();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const loadRules = async (deviceId) => {
    try {
      const res = await api.get(`/devices/${deviceId}/rules`);
      setRules(res.data);
      setShowRules(deviceId);
    } catch (e) { console.error(e); }
  };

  const saveRules = async () => {
    try {
      await api.put(`/devices/${showRules}/rules`, { ...rules, device_id: showRules });
      setShowRules(null);
      alert('Rules saved!');
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="devices-page" className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Parental Controls</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Device Manager</h1>
          <p className="text-sm text-neutral-500 mt-1">Control child devices, LED TVs, screen time & bedtime rules</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-device-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Add Device
        </button>
      </div>

      {/* Add Device Form */}
      {showForm && (
        <div className="glass-card p-6 animate-fade-up" data-testid="device-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">Register Device</h3>
            <button onClick={() => setShowForm(false)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Device Name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="device-name-input"
                placeholder="e.g., Arjun's Phone" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
            </div>
            <div>
              <label className="overline block mb-2">Type</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} data-testid="device-type-select"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                <option value="android_phone">Android Phone</option>
                <option value="android_tablet">Tablet</option>
                <option value="led_tv">LED TV</option>
                <option value="smart_speaker">Smart Speaker</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Owner / Child Name</label>
              <input value={form.owner} onChange={e => setForm(p => ({ ...p, owner: e.target.value }))} placeholder="e.g., Arjun"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Room</label>
              <input value={form.room} onChange={e => setForm(p => ({ ...p, room: e.target.value }))} placeholder="e.g., Kids Room"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">IP Address (optional)</label>
              <input value={form.ip_address} onChange={e => setForm(p => ({ ...p, ip_address: e.target.value }))} placeholder="192.168.1.x"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none font-mono" />
            </div>
            <div>
              <label className="overline block mb-2">MAC Address (optional)</label>
              <input value={form.mac_address} onChange={e => setForm(p => ({ ...p, mac_address: e.target.value }))} placeholder="AA:BB:CC:DD:EE:FF"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none font-mono" />
            </div>
          </div>
          <button onClick={handleAdd} data-testid="save-device-btn"
            className="mt-4 px-6 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold">Save Device</button>
        </div>
      )}

      {/* Screen Time Rules Modal */}
      {showRules && (
        <div className="glass-card p-6 animate-fade-up border-l-2 border-[#00E5FF]/40" data-testid="rules-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Screen Time Rules</h3>
            <button onClick={() => setShowRules(null)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="overline block mb-2">Daily Limit (minutes)</label>
              <input type="number" value={rules.daily_limit_minutes} min={0} max={1440} data-testid="daily-limit-input"
                onChange={e => setRules(p => ({ ...p, daily_limit_minutes: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
              <p className="text-[10px] text-neutral-500 mt-1">{Math.floor(rules.daily_limit_minutes / 60)}h {rules.daily_limit_minutes % 60}m</p>
            </div>
            <div>
              <label className="overline block mb-2">Bedtime Start</label>
              <input type="time" value={rules.bedtime_start} data-testid="bedtime-start-input"
                onChange={e => setRules(p => ({ ...p, bedtime_start: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Bedtime End</label>
              <input type="time" value={rules.bedtime_end} data-testid="bedtime-end-input"
                onChange={e => setRules(p => ({ ...p, bedtime_end: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
          </div>
          <button onClick={saveRules} data-testid="save-rules-btn"
            className="mt-4 px-6 py-2.5 rounded-full bg-[#00E5FF] text-black text-sm font-semibold">Save Rules</button>
        </div>
      )}

      {/* Device Grid */}
      {devices.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <DeviceMobile size={40} weight="duotone" className="text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-500 text-sm">No devices registered. Add your family's devices to manage screen time and control.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {devices.map(dev => {
            const Icon = TYPE_ICONS[dev.type] || WifiHigh;
            const isTV = dev.type === 'led_tv';
            return (
              <div key={dev.device_id} data-testid={`device-${dev.device_id}`}
                className="glass-card p-5 animate-fade-up">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dev.is_locked ? 'bg-red-500/10' : 'bg-[#FF6B35]/10'}`}>
                      <Icon size={20} className={dev.is_locked ? 'text-red-400' : 'text-[#FF6B35]'} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{dev.name}</p>
                      <p className="text-[10px] text-neutral-500">{TYPE_LABELS[dev.type]} {dev.owner && `| ${dev.owner}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${dev.status === 'online' ? 'bg-[#22C55E] shadow-[0_0_6px_rgba(34,197,94,0.5)]' : dev.status === 'off' ? 'bg-neutral-600' : 'bg-yellow-500'}`} />
                    <span className="text-[10px] text-neutral-500">{dev.status}</span>
                  </div>
                </div>

                {dev.room && <p className="text-[11px] text-neutral-500 mb-3">Room: {dev.room}</p>}

                {dev.is_locked && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 mb-3">
                    <Lock size={12} className="text-red-400" />
                    <span className="text-[11px] text-red-400 font-medium">LOCKED</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5 mt-auto">
                  <button onClick={() => handleCommand(dev.device_id, dev.is_locked ? 'unlock' : 'lock')}
                    data-testid={`${dev.is_locked ? 'unlock' : 'lock'}-${dev.device_id}`}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${
                      dev.is_locked ? 'border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10' : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                    }`}>
                    {dev.is_locked ? <><LockOpen size={10} className="inline mr-1" />Unlock</> : <><Lock size={10} className="inline mr-1" />Lock</>}
                  </button>

                  {isTV && (
                    <>
                      <button onClick={() => handleCommand(dev.device_id, dev.status === 'off' ? 'power_on' : 'power_off')}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-white/10 text-neutral-400 hover:text-white transition-all">
                        <Power size={10} className="inline mr-1" />{dev.status === 'off' ? 'Power On' : 'Power Off'}
                      </button>
                      <button onClick={() => handleCommand(dev.device_id, 'volume_up')}
                        className="px-2 py-1.5 rounded-lg text-[10px] font-semibold border border-white/10 text-neutral-400 hover:text-white">Vol+</button>
                      <button onClick={() => handleCommand(dev.device_id, 'volume_down')}
                        className="px-2 py-1.5 rounded-lg text-[10px] font-semibold border border-white/10 text-neutral-400 hover:text-white">Vol-</button>
                      <button onClick={() => handleCommand(dev.device_id, 'mute')}
                        className="px-2 py-1.5 rounded-lg text-[10px] font-semibold border border-white/10 text-neutral-400 hover:text-white">Mute</button>
                    </>
                  )}

                  <button onClick={() => loadRules(dev.device_id)}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-all">
                    <Clock size={10} className="inline mr-1" />Rules
                  </button>

                  <button onClick={() => handleDelete(dev.device_id)}
                    className="px-2 py-1.5 rounded-lg text-[10px] text-neutral-600 hover:text-red-400 transition-all">
                    <Trash size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
